import { createElement, ReactNode } from 'react';
import { t, TOptions } from 'i18next';
import dompurify from 'dompurify';

const nonFormattedKeys = new Set<string>();

type Options = TOptions & {
  replacers?: Record<string, (n: ChildNode) => ReactNode>;
} & Record<string, unknown>;

const tagRe = /<[^>]+>[^<]*<\/[^>]+>/im;

interface Translator {
  (key: string, options?: TOptions & Record<string, unknown>): string;
  (key: string, options: TOptions & Record<string, unknown>, markup: true): ReactNode;
  (key: string, markup: true): ReactNode;
}

const AllowedNodes = new Set([1, 3]);

function convert(n: ChildNode, i: number, options?: Options): ReactNode {
  // @TODO Implement attribute parsing
  switch (n.nodeType) {
    case 3: return n.textContent;
    case 1: {
      const tagName = n.nodeName.toLowerCase();
      if (options && options?.replacers && options.replacers[tagName]) {
        return options.replacers[tagName](n);
      }
      return createElement(
        tagName,
        { key: `ch${i}` },
        Array.from(n.childNodes).filter((n) => AllowedNodes.has(n.nodeType)).map((cn, j) => convert(cn, j, options))
      )
    };
    default: return null;
  }
}

const translate: Translator = (key: string, options?: Options | true, markup?: true): any => {
  markup = (typeof options === 'boolean') ? options : markup;
  options = (typeof options === 'object') ? options : undefined;
  const v = t(key, options);
  if (!markup || nonFormattedKeys.has(key)) return v;
  if (!tagRe.test(v)) {
    nonFormattedKeys.add(key);
    return v;
  }
  const parser = new DOMParser();
  const fragment = parser.parseFromString(dompurify.sanitize(v), 'text/html');
  return Array.from(fragment.body.childNodes.values()).filter((n) => AllowedNodes.has(n.nodeType)).map((n, i) => convert(n, i, options));
}

export default translate;